'use client'

import '@/components/eligibility/Eligibility.css'
import '@/sections/sections.css'

import type { ChangeEvent, FocusEvent } from 'react'
import type { CardType } from './types'
import { ProfitRates } from '@/constants/dropdown'

import { useEffect, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import parse from 'html-react-parser'

import CashbackIcon from '@/assets/images/bag_icon.svg'
import CalculatorPlus from '@/assets/images/calculator_plus.svg'
import Calculator from '@/assets/images/calculator.svg'
import MoneyBag from '@/assets/images/money_bag.svg'
import PercentageIcon from '@/assets/images/percentage_icon.svg'
import { useBootstrapTooltips } from '@/hooks/useBootstrapTooltips'
import { useIsMobile } from '@/hooks/useIsMobile'
import { formatCurrency, handleObjectCurrencyInput, handleSimpleCurrencyInput } from '@/utils'
import { calculateCashback, simulateCardPayoff } from '@/utils/cardCalculator'

type CardCalculatorProps = {
  /** Rich-text heading, parsed with html-react-parser. */
  title: string
  subtext: string
  /** Cashback rate (percent) on the premium spend categories. */
  premiumCashback: number
  /** Cashback rate (percent) on everything else. */
  generalCashback: number
  /** Monthly cashback ceiling in RM, applied to the premium categories only. */
  maxCap: number
  cardType: CardType
}

export const CardCalculator = ({
  title,
  subtext,
  premiumCashback,
  generalCashback,
  maxCap,
  cardType
}: CardCalculatorProps) => {
  const [cashback, setCashback] = useState({
    premium: 0,
    general: 0
  })
  const [monthlySpending, setMonthlySpending] = useState<{
    premium: string | null
    general: string | null
  }>({
    premium: null,
    general: null
  })
  const [payment, setPayment] = useState<number | string>()
  const [noOfMonths, setNoOfMonths] = useState<number>()
  const [currentBalance, setCurrentBalance] = useState(0)
  const [currentBalanceText, setCurrentBalanceText] = useState<string>()
  const [profitRate, setProfitRate] = useState(cardType === 'platinum' ? 15 : 18)
  const [minMonthlyPayment, setMinMonthlyPayment] = useState(0)
  const [additionalMonthlyPayment, setAdditionalMonthlyPayment] = useState(0)
  const [additionalMonthlyPaymentText, setAdditionalMonthlyPaymentText] = useState<string>()

  const [selectedButton, setSelectedButton] = useState('cashback calculator')
  // useIsMobile (mounted-gated), not raw useMediaQuery: the static export
  // bakes false into the HTML and the raw hook can stay stuck on it,
  // shipping the desktop border/min-height branch to phones.
  const isTab = useIsMobile({ maxWidth: 767 })

  const handleClear = () => {
    setMonthlySpending({
      premium: null,
      general: null
    })
    setCashback({
      premium: 0,
      general: 0
    })
    setPayment(0)
    setNoOfMonths(0)
    setCurrentBalance(0)
    setProfitRate(cardType === 'platinum' ? 15 : 18)
    setMinMonthlyPayment(0)
  }

  useEffect(() => {
    handleClear()
  }, [premiumCashback])

  const CalculateTotal = (balanceInput: number, aprInputPercent: number, additionalPay: number) => {
    const { totalInterest, months, firstMonthMinPayment } = simulateCardPayoff(
      balanceInput,
      aprInputPercent,
      additionalPay
    )

    if (months === 0) {
      setPayment('0.00')
      setNoOfMonths(0)
      setMinMonthlyPayment(0)
      return
    }

    setPayment(formatCurrency(totalInterest)) // e.g., "157.95" or "491.27"
    setNoOfMonths(months) // e.g., 24 or 42
    setMinMonthlyPayment(firstMonthMinPayment)
  }

  useEffect(() => {
    CalculateTotal(currentBalance, profitRate, additionalMonthlyPayment)
  }, [currentBalance, profitRate, additionalMonthlyPayment])

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name
    const fieldName = name.includes('premium') ? 'premium' : 'general'

    handleObjectCurrencyInput(
      event.target.value,
      fieldName,
      monthlySpending,
      setMonthlySpending,
      (numericValue: number | null, field: string) => {
        if (field === 'premium') {
          // null means the field was cleared; 0 spend earns 0 cashback.
          const convertedValue = calculateCashback(numericValue ?? 0, premiumCashback, maxCap)
          setCashback(prev => ({ ...prev, premium: convertedValue }))
        } else {
          setCashback(prev => ({
            ...prev,
            general: calculateCashback(numericValue ?? 0, generalCashback)
          }))
        }
      }
    )
  }

  // Optional blur handler for 2 decimal places
  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    const name = event.target.name
    const fieldName = name.includes('premium') ? 'premium' : 'general'

    if (monthlySpending[fieldName]) {
      const num = Number.parseFloat((monthlySpending[fieldName] ?? '').replaceAll(',', '')) || 0
      const formatted = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(num)

      setMonthlySpending(prev => ({
        ...prev,
        [fieldName]: formatted
      }))
    }
  }

  useBootstrapTooltips()
  return (
    <section id="card-calculator" className="values section h-100 px-0 px-md-3">
      <Container className="d-flex flex-column items-center">
        <Row className="mb-4 mx-auto text-center" style={{ color: 'var(--alrajhi-black)' }}>
          <h2 data-aos="fade-up" className="feature-title mb-0">
            {parse(title)}
          </h2>
          <span data-aos="fade-up">{parse(subtext)}</span>
        </Row>
        <Row>
          <div className="calculator-container">
            <div
              className="toggle-slider-container w-100 bg-white"
              style={{
                borderRadius: '75px',
                border: isTab ? '8px solid white' : '3px solid white'
              }}
            >
              <div
                className="toggle-slider"
                style={{
                  transform: `translateX(${
                    selectedButton === 'cashback calculator' ? '0%' : '100%'
                  })`,
                  borderRadius: '75px'
                }}
              />
              {['Cashback calculator', 'Payment calculator']?.map(item => (
                <div
                  key={item}
                  className={`sliding-button`}
                  style={{
                    color:
                      selectedButton === item.toLocaleLowerCase()
                        ? '#FFFFFF'
                        : 'var(--alrajhi-black)',
                    fontWeight: 400,
                    minHeight: isTab ? '42px' : '75px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onClick={() => {
                    setSelectedButton(item.toLocaleLowerCase())
                  }}
                >
                  {item}
                </div>
              ))}
            </div>
            {selectedButton === 'cashback calculator' ? (
              <Row>
                <Col
                  lg={6}
                  xs={12}
                  className="bg-white bg-lg-transparent pt-lg-0 pb-lg-0 rounded-3"
                  style={{ paddingTop: '12px', paddingBottom: '12px' }}
                >
                  <Row className="bg-white rounded-3 p-lg-4 p-0 m-0 mb-4">
                    <div className="d-flex align-items-center gap-2 mb-4">
                      <img src={CashbackIcon.src} alt="Cashback Icon" />
                      <p className="primary-color fw-bold mb-0" style={{ marginTop: '5px' }}>
                        {premiumCashback}% Cashback
                      </p>
                    </div>

                    <div
                      style={{
                        backgroundColor: '#F5FAFF',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        marginBottom: '30px'
                      }}
                    >
                      <div className="d-flex justify-content-between gap-2">
                        <p style={{ color: 'var(--grey-500-body-text)' }} className="fs-16 mb-1">
                          {cardType === 'platinum'
                            ? 'Dining · Groceries · Online · Utilities'
                            : 'Overseas spend'}
                        </p>

                        <button
                          type="button"
                          className="btn btn-link p-0"
                          data-bs-toggle="tooltip"
                          data-bs-placement="top"
                          title="Transactions made in a currency other than the
                                Malaysian Ringgit currency code – MYR"
                          aria-label="Transactions info"
                          data-bs-custom-class="wide-tooltip"
                          style={{ color: 'var(--grey-500)' }}
                        >
                          <i className="bi bi-question-circle" aria-hidden="true"></i>
                        </button>
                      </div>
                      <span
                        className="primary-color"
                        style={{ fontFamily: 'var(--font-ping)', fontWeight: 500 }}
                      >
                        Maximum RM{maxCap} cashback per month
                      </span>
                    </div>
                    <div>
                      <p className="mb-2">Monthly spending</p>
                      <div className="position-relative">
                        <span
                          className="position-absolute top-50 translate-middle-y"
                          style={{
                            left: '0.75rem',
                            fontWeight: 'bold',
                            color: '#000000ff'
                          }}
                        >
                          RM
                        </span>

                        <input
                          type="text"
                          className="form-control black-placeholder"
                          placeholder="0.00"
                          name="premium"
                          id="premium"
                          style={{ paddingLeft: '2.2rem', fontWeight: 'bold' }}
                          required
                          value={monthlySpending.premium ?? ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          aria-label="Monthly Spending"
                          maxLength={11}
                        />
                      </div>
                    </div>
                  </Row>
                  <Row className="monthly-cashback-container">
                    <p>Monthly cashback</p>
                    <span>RM {cashback.premium ? formatCurrency(cashback.premium) : '0.00'}</span>
                  </Row>
                </Col>
                <Col
                  lg={6}
                  xs={12}
                  className="bg-white bg-lg-transparent pt-lg-0 pb-lg-0 rounded-3 mt-4 mt-lg-0"
                  style={{ paddingTop: '12px', paddingBottom: '12px' }}
                >
                  <Row className="bg-white rounded-3 p-lg-4 p-0 m-0 mb-4">
                    <div className="d-flex align-items-center gap-2 mb-4">
                      <img src={CashbackIcon.src} alt="Cashback Icon" />
                      <p className="primary-color fw-bold" style={{ marginBottom: '-7px' }}>
                        {generalCashback}% Cashback
                      </p>
                    </div>

                    <div
                      style={{
                        backgroundColor: '#F5FAFF',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        marginBottom: '30px'
                      }}
                    >
                      <div className="d-flex justify-content-between gap-2">
                        <p style={{ color: 'var(--grey-500-body-text)' }} className="fs-16 mb-1">
                          {cardType === 'platinum'
                            ? 'Other retail transactions'
                            : 'Local retail spend'}
                        </p>

                        <button
                          type="button"
                          className="btn btn-link p-0"
                          data-bs-toggle="tooltip"
                          data-bs-placement="right"
                          title="Please refer to the Cashback T&C for the list of ineligible MCCs"
                          aria-label="Transactions info"
                          data-bs-custom-class="wide-tooltip"
                          style={{ color: 'var(--grey-500)' }}
                        >
                          <i className="bi bi-question-circle" aria-hidden="true"></i>
                        </button>
                      </div>
                      <span
                        className="primary-color"
                        style={{ fontFamily: 'var(--font-ping)', fontWeight: 500 }}
                      >
                        Unlimited cashback
                      </span>
                    </div>
                    <div>
                      <p className="mb-2">Monthly spending</p>
                      <div className="position-relative">
                        <span
                          className="position-absolute top-50 translate-middle-y"
                          style={{
                            left: '0.75rem',
                            fontWeight: 'bold',
                            color: '#000000ff'
                          }}
                        >
                          RM
                        </span>

                        <input
                          type="text"
                          className="form-control black-placeholder"
                          placeholder="0.00"
                          name="general"
                          id="general"
                          style={{ paddingLeft: '2.2rem', fontWeight: 'bold' }}
                          required
                          value={monthlySpending.general ?? ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          aria-label="Monthly Spending"
                          maxLength={11}
                        />
                      </div>
                    </div>
                  </Row>
                  <Row className="monthly-cashback-container">
                    <p>Monthly cashback</p>
                    <span>RM {cashback.general ? formatCurrency(cashback.general) : '0.00'}</span>
                  </Row>
                </Col>
              </Row>
            ) : (
              <Col className="bg-white bg-lg-transparent rounded-3" style={{ padding: '12px' }}>
                <Row className="">
                  <div className="mb-4">
                    <div className="d-flex gap-2">
                      <div>
                        <img src={MoneyBag.src} alt="Cashback Icon" width={16} height={16} />
                      </div>
                      <p className="mb-2">Current outstanding balance</p>
                    </div>
                    <div className="position-relative">
                      <span
                        className="position-absolute top-50 translate-middle-y"
                        style={{
                          left: '0.75rem',
                          fontWeight: 'bold',
                          color: '#000000ff'
                        }}
                      >
                        RM
                      </span>
                      <input
                        type="text"
                        className="form-control black-placeholder"
                        name="currentBalance"
                        id="currentBalance"
                        placeholder="0.00"
                        style={{ paddingLeft: '2.2rem', fontWeight: 'bold' }}
                        required
                        value={currentBalanceText}
                        onChange={e => {
                          handleSimpleCurrencyInput(
                            e.target.value,
                            setCurrentBalanceText,
                            setCurrentBalance
                          )
                        }}
                        onBlur={() => {
                          if (currentBalanceText) {
                            const num =
                              Number.parseFloat(currentBalanceText.replaceAll(',', '')) || 0
                            const formatted = new Intl.NumberFormat('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            }).format(num)
                            setCurrentBalanceText(formatted)
                            setCurrentBalance(num)
                          }
                        }}
                        inputMode="decimal"
                        aria-label="Current Balance"
                        maxLength={11}
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <div className="d-flex gap-2">
                      <div>
                        <img src={PercentageIcon.src} alt="Cashback Icon" width={16} height={16} />
                      </div>
                      <p className="mb-2">Annual profit rate</p>
                    </div>
                    <select
                      className="form-select fw-bold"
                      name="annualProfitRate"
                      id="annualProfitRate"
                      required
                      value={profitRate}
                      onChange={e => {
                        if (/^\d*$/.test(e.target.value)) {
                          setProfitRate(Number(e.target.value))
                        }
                      }}
                    >
                      {ProfitRates.map(item => (
                        <option key={item.value} value={item.value} disabled={item.disabled}>
                          {parse(item.text)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-4">
                    <div className="d-flex gap-2">
                      <div>
                        <img src={Calculator.src} alt="Cashback Icon" width={16} height={16} />
                      </div>
                      <p className="mb-2">Monthly payment amount</p>
                    </div>
                    <div className="position-relative">
                      <span
                        className="position-absolute top-50 translate-middle-y"
                        style={{
                          left: '0.75rem',
                          fontWeight: 'bold',
                          color: '#000000ff'
                        }}
                      >
                        RM
                      </span>

                      <input
                        type="text"
                        className="form-control"
                        placeholder="0.00"
                        name="minMonthlyPayment"
                        id="minMonthlyPayment"
                        style={{ paddingLeft: '2.2rem', fontWeight: 'bold' }}
                        required
                        value={formatCurrency(minMonthlyPayment)}
                        // onChange={(e) => {
                        //   if (/^\d*$/.test(e.target.value)) {
                        //     setMinMonthlyPayment(e.target.value);
                        //   }
                        // }}
                        aria-label="Min Monthly Payment"
                        disabled
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <div className="d-flex gap-2">
                      <div>
                        <img src={CalculatorPlus.src} alt="Cashback Icon" width={18} height={18} />
                      </div>
                      <p className="mb-2">Additional monthly payment amount</p>
                    </div>
                    <div className="position-relative">
                      <span
                        className="position-absolute top-50 translate-middle-y"
                        style={{
                          left: '0.75rem',
                          fontWeight: 'bold',
                          color: '#000000ff'
                        }}
                      >
                        RM
                      </span>

                      <input
                        type="text"
                        className="form-control black-placeholder"
                        name="addMinMonthlyPayment"
                        id="addMinMonthlyPayment"
                        placeholder="0.00"
                        style={{ paddingLeft: '2.2rem', fontWeight: 'bold' }}
                        required
                        value={additionalMonthlyPaymentText}
                        onChange={e => {
                          handleSimpleCurrencyInput(
                            e.target.value,
                            setAdditionalMonthlyPaymentText,
                            setAdditionalMonthlyPayment
                          )
                        }}
                        onBlur={() => {
                          if (additionalMonthlyPaymentText) {
                            const num =
                              Number.parseFloat(additionalMonthlyPaymentText.replaceAll(',', '')) ||
                              0
                            const formatted = new Intl.NumberFormat('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            }).format(num)
                            setAdditionalMonthlyPaymentText(formatted)
                            setAdditionalMonthlyPayment(num)
                          }
                        }}
                        maxLength={11}
                        aria-label="Min Monthly Payment"
                      />
                    </div>
                  </div>
                </Row>
                <Row className="mt-4">
                  <Col lg={6} xs={12}>
                    <Row className="monthly-cashback-container">
                      <p>Total months to pay off</p>
                      <span>{noOfMonths} months</span>
                    </Row>
                  </Col>
                  <Col lg={6} xs={12} className="mt-4 mt-lg-0">
                    <Row className="monthly-cashback-container">
                      <p>Estimated total profit charged</p>
                      <span>RM {payment}</span>
                    </Row>
                  </Col>
                </Row>
              </Col>
            )}
          </div>
        </Row>
      </Container>
    </section>
  )
}
